import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { logger } from '../utils/logger';

// Cart item interface - same as in App.tsx
export interface CartItem {
  id: string;
  handle?: string; // For getting localized product data
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  quantity: number;
  weight?: string;
  variant?: {
    weight: string;
    unit?: string;
    price: number;
    pricePerKg?: number;
  };
}

interface CartStore {
  // State
  cartItems: CartItem[];
  isCartOpen: boolean;

  // Actions
  addToCart: (product: any, variant?: any) => void;
  updateCartItemQuantity: (id: string, quantity: number, variant?: any) => void;
  removeFromCart: (id: string, variant?: any) => void;
  clearCart: () => void;
  setCartOpen: (isOpen: boolean) => void;

  // Getters
  getTotalItems: () => number;
  getTotalPrice: () => number;
  getCartItemsCount: () => number;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      // Initial State
      cartItems: [],
      isCartOpen: false,

      // Actions
      addToCart: (product: any, variant?: any) => {
        const cartItems = get().cartItems;
        const existingItemIndex = cartItems.findIndex(
          item =>
            item.id === product.id && item.variant?.weight === variant?.weight
        );

        if (existingItemIndex > -1) {
          // Update existing item quantity
          const updatedItems = [...cartItems];
          updatedItems[existingItemIndex].quantity += 1;
          set({ cartItems: updatedItems });
          logger.log('🛒 CartStore: Updated quantity for', product.name);
        } else {
          // Add new item
          const price = variant?.price ?? product.price ?? 0;
          const newItem: CartItem = {
            id: product.id,
            handle: product.handle,
            name: product.name,
            price: price,
            originalPrice: product.originalPrice,
            image: product.image,
            quantity: 1,
            weight: variant?.weight,
            variant: variant,
          };

          set({ cartItems: [...cartItems, newItem] });
          logger.log('🛒 CartStore: Added new item', product.name);
        }
      },

      updateCartItemQuantity: (id: string, quantity: number, variant?: any) => {
        if (quantity <= 0) {
          get().removeFromCart(id, variant);
          return;
        }

        const cartItems = get().cartItems;
        const updatedItems = cartItems.map(item =>
          item.id === id && item.variant?.weight === variant?.weight
            ? { ...item, quantity }
            : item
        );

        set({ cartItems: updatedItems });
        logger.log('🛒 CartStore: Updated quantity for', id, 'to', quantity);
      },

      removeFromCart: (id: string, variant?: any) => {
        const cartItems = get().cartItems;
        const updatedItems = cartItems.filter(
          item => !(item.id === id && item.variant?.weight === variant?.weight)
        );

        set({ cartItems: updatedItems });
        logger.log('🛒 CartStore: Removed item', id);
      },

      clearCart: () => {
        set({ cartItems: [] });
        logger.log('🛒 CartStore: Cart cleared');
      },

      setCartOpen: (isOpen: boolean) => {
        set({ isCartOpen: isOpen });
      },

      // Getters
      getTotalItems: () => {
        return get().cartItems.reduce(
          (total, item) => total + item.quantity,
          0
        );
      },

      getTotalPrice: () => {
        return get().cartItems.reduce(
          (total, item) => total + item.price * item.quantity,
          0
        );
      },

      getCartItemsCount: () => {
        return get().cartItems.length;
      },
    }),
    {
      name: 'olispices-cart-store',
      partialize: state => ({
        cartItems: state.cartItems,
        // isCartOpen не сохраняем - это UI состояние
      }),
    }
  )
);

// Convenience hooks
export const useCartItems = () => useCartStore(state => state.cartItems);
export const useCartOpen = () => useCartStore(state => state.isCartOpen);
export const useCartActions = () =>
  useCartStore(state => ({
    addToCart: state.addToCart,
    updateCartItemQuantity: state.updateCartItemQuantity,
    removeFromCart: state.removeFromCart,
    clearCart: state.clearCart,
    setCartOpen: state.setCartOpen,
  }));
export const useCartTotals = () =>
  useCartStore(state => ({
    totalItems: state.getTotalItems(),
    totalPrice: state.getTotalPrice(),
    itemsCount: state.getCartItemsCount(),
  }));
